<template>
  <v-row class="pa-0 ma-0 fill-height elevation-1 white" :style="{ 'min-height': '600px' }" no-gutters>
    <v-progress-linear v-if="loading" id="map-loader" :indeterminate="true" />
    <v-col id="map" cols="12" class="pa-0 ma-0">
      <div id="mapbox-wrapper" />
    </v-col>
    <div class="total-ads">Total : {{ totatAds }}</div>
    <div class="active-only">
      <v-switch dark dense hide-details class="ma-2 white--text" label="Annonces encore actives"
        :input-value="activeOnly" @change="toggleActiveOnly" />
    </div>
  </v-row>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import clientApi from '@/api/clientApi'
import promisify from 'map-promisified'
import { filter, find, map as Map } from 'lodash-es'
import Mapbox from 'mapbox-gl'
import bbox from '@turf/bbox'
import mask from '@turf/mask'
import centroid from '@turf/centroid'

export default {
  name: 'TSDashboardMap',
  props: {
    adsType: {
      type: String,
      default: "ads",
    },
  },
  components: {},
  data() {
    return {
      lastZoom: 8,
      mapStyle: 'mapbox://styles/mapbox/light-v10',
      mapbox: null,
      emptyFeatures: { type: 'FeatureCollection', features: [] },
      itemsSource: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      parcellesSource: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      deptSource: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      deptPoint: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      deptBoundarySource: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      useBdOrtho: false,
      bdOrtho: {
        type: 'raster',
        tiles: [
          'https://data.geopf.fr/wmts?layer=ORTHOIMAGERY.ORTHOPHOTOS&style=normal&tilematrixset=PM&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}',
        ],
        tileSize: 256,
        attribution: 'bdOrtho',
      },
      bdOrthoTiles: {
        id: 'bdOrthoTiles',
        type: 'raster',
        source: 'bdOrtho',
        minzoom: 14,
        layout: {
          visibility: this.useBdOrtho ? 'visible' : 'none',
        },
      },
      items: {
        id: 'items',
        type: 'circle',
        source: 'items',
        paint: {
          'circle-radius': 6,
          'circle-color': [
            'match',
            ['get', 'source'],
            'leboncoin',
            '#FF6E14',
            'bienici',
            '#FBBA20',
            'seloger',
            '#E00030',
            'bellesdemeures',
            '#AED581',
            'lefigaro',
            '#0096c7',
            'proprietes_lefigaro',
            '#003161',
            '#ccc',
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': [
            'match',
            ['to-string', ['get', 'isActive']],
            'true',
            '#34c2b8',
            'false',
            '#ff5252',
            '#fff',
          ],
        },
        filter: ['==', '$type', 'Point'],
      },
      parcelles: {
        id: 'parcelles',
        type: 'line',
        source: 'parcelles',
        minzoom: 14,
        paint: {
          'line-width': 2,
          'line-color': [
            'match',
            ['to-string', ['get', 'isActive']],
            'true',
            '#34c2b8',
            'false',
            '#ff5252',
            /* other */ '#ccc',
          ],
        },
      },
      deptsBoundaries: {
        id: 'deptsBoundaries',
        type: 'fill',
        source: 'dept',
        paint: {
          'fill-color': {
            property: 'total',
            stops: [
              [0, '#fff'],
              [200, '#ff9800'],
              [2000, '#fb0000'],
            ],
          },
          'fill-outline-color': '#14617e',
        },
      },
      deptLayer: {
        id: 'deptLayer',
        type: 'symbol',
        source: 'deptPoint',
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': 'rgba(0, 0, 0, 0.7)',
          'text-halo-width': 1,
          'text-halo-blur': 1,
        },
        layout: {
          'text-field': ['get', 'total'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      },
      deptPolygon: {
        id: 'hintPolygon',
        type: 'fill',
        source: 'deptBoundary',
        paint: {
          'fill-color': 'rgba(20, 97, 126, 0.5)',
          'fill-outline-color': '#14617e',
        },
      },
      deptStroke: {
        id: 'hintStroke',
        type: 'line',
        source: 'deptBoundary',
        paint: {
          'line-width': 2,
          'line-color': '#83F2BE',
        },
      },
      popup: new Mapbox.Popup({
        closeButton: false,
        closeOnClick: false,
      }),
      selectedDept: null,
      parcellesFeatures: null,
      totatAds: 0,
      activeOnly: true,
      loading: true,
    }
  },
  async mounted() {
    const mapContainer = document.querySelector('#mapbox-container');
    const mapContainerDest = document.querySelector('#mapbox-wrapper');
    mapContainerDest.appendChild(mapContainer);

    if (this.$root.map) {
      this.initMap()
    } else {
      this.$root.$on('mapboxReady', async () => {
        this.initMap()
      })
    }

    this.$root.$on('resizeMap', this.resize)
  },
  destroyed() {
    this.$root.$off('resizeMap', this.resize)
    this.resetLayers();
  },
  watch: {
    async adsType() {
      this.loading = true
      const params = { active: this.activeOnly, adsType: this.adsType }
      await this.getStats({ params })
      this.loading = false
      this.resetLayers();
      const mapContainer = document.querySelector('#mapbox-container');
      const mapContainerDest = document.querySelector('#mapbox-wrapper');
      mapContainerDest.appendChild(mapContainer);
      this.setLayers(this.$root.map);
    },
  },
  computed: {
    ...mapGetters({
      stats: 'stats/stats',
      adsTypeState: 'stats/adsTypeState',
    }),
  },
  methods: {
    ...mapActions({
      getStats: 'stats/getStats',
      getFluxItem: 'fluxItems/getFluxItem',
    }),
    async initMap() {
      this.$root.map.resize()
      this.$root.map.setCenter([2.2096669, 46.232192])
      this.$root.map.setZoom(4.4)
      if (!this.stats || this.adsTypeState !== this.adsType) {
        const params = { active: true, adsType: this.adsType }
        await this.getStats({ params })
      }

      this.setLayers(this.$root.map)
      this.$emit('loaded')
      this.loading = false
    },
    async fitToBounds(geom) {
      const bounds = bbox(geom)

      const fitToPromisified = promisify(this.$root.map, 'fitBounds')
      await fitToPromisified(bounds, {
        speed: 3,
        padding: 40,
      })
    },
    moveLayers(map) {
      map.moveLayer('bdOrthoTiles', 'tunnel-major-link-case')
      const layerOrder = ['deptPolygon', 'deptStroke']
      layerOrder.forEach((l) => {
        if (map.getLayer(l)) {
          map.moveLayer(l)
        }
      })
    },
    formatDept(feature) {
      const properties = { ...feature.properties }
      delete properties.code
      let propertiesHTML = `<h3 class="mb-3">Département: ${feature.properties.code}</h3><ul class="properties ma-0 pa-0">`
      propertiesHTML += '</ul>'

      return propertiesHTML
    },
    formatItem(feature) {
      const properties = { ...feature.properties }

      let propertiesHTML = `<h3 class="mb-3">Annonce ${feature.properties.isActive ? 'active' : 'inactive'
        }</h3><ul class="properties ma-0 pa-0">`
      propertiesHTML += `<li class="pa-2"><b class="text-capitalize">${properties.title}</b></li>`
      propertiesHTML += `<li class="pa-2"><b class="text-capitalize">Adresse</b> : ${properties.finalAddress}</li>`
      propertiesHTML += `<li class="pa-2"><b class="text-capitalize">Prix</b> : ${properties.price} €</li>`
      propertiesHTML += `<li class="pa-2"><b class="text-capitalize">Superficies</b> : ${properties.area} m² habitables - ${properties.landArea} m² de terrain</li>`
      propertiesHTML += '</ul>'

      return propertiesHTML
    },
    resize() {
      setTimeout(() => {
        this.$root.map.resize()
      }, 100)
    },
    setCursor(map, value) {
      map.getCanvas().style.cursor = value
    },
    resetLayers(){
      this.$root.map.off('click', 'deptsBoundaries', this.onDeptClick)
      this.$root.map.off('mouseenter', 'deptLayer', this.onDeptEnter)
      this.$root.map.off('mouseleave', 'deptLayer', this.onDeptLeave)
      this.$root.map.off('click', 'hintPolygon', this.outsideClick)
      this.$root.map.off('mouseenter', 'items', this.onItemEnter)
      this.$root.map.off('mouseleave', 'items', this.onItemLeave)
      this.$root.map.off('click', 'items', this.onItemClick)

      // remove Layers
      const layers = ['bdOrthoTiles', 'items', 'parcelles', 'deptsBoundaries', 'deptLayer', 'hintPolygon', 'hintStroke']
      layers.forEach(l => {
        if (this.$root.map.getLayer(l)) {
          this.$root.map.removeLayer(l)
        }
      })
      // remove sources
      const sources = ['dept', 'deptBoundary', 'deptPoint', 'parcelles', 'items', 'bdOrtho']
      sources.forEach(s => {
        if (this.$root.map.getSource(s)) {
          this.$root.map.removeSource(s)
        }
      })
    },
    setLayers(map) {
      this.totatAds = this.stats.depts.reduce((v, dep) => {
        if (dep.total) {
          v += parseInt(dep.total)
        }

        return v
      }, 0)
      // sources
      map.addSource('bdOrtho', this.bdOrtho)
      map.addSource('items', this.itemsSource)
      map.addSource('parcelles', this.parcellesSource)
      map.addSource('dept', this.deptSource)
      map.addSource('deptPoint', this.deptPoint)
      map.addSource('deptBoundary', this.deptBoundarySource)

      // layers
      map.addLayer(this.bdOrthoTiles)
      map.addLayer(this.items)
      map.addLayer(this.parcelles)
      map.addLayer(this.deptsBoundaries)
      map.addLayer(this.deptLayer)
      map.addLayer(this.deptPolygon)
      map.addLayer(this.deptStroke)

      //events
      map.on('click', 'deptsBoundaries', this.onDeptClick)
      map.on('mouseenter', 'deptLayer', this.onDeptEnter)
      map.on('mouseleave', 'deptLayer', this.onDeptLeave)
      map.on('click', 'hintPolygon', this.outsideClick)
      map.on('mouseenter', 'items', this.onItemEnter)
      map.on('mouseleave', 'items', this.onItemLeave)
      map.on('click', 'items', this.onItemClick)

      setTimeout(() => {
        const features = Map(
          filter(this.stats.depts, (s) => {
            return s.total
          }),
          (s) => {
            const center = s.geomCenter
            return {
              type: 'Feature',
              geometry: s.geom,
              properties: {
                total: parseInt(s.total),
                code: s.codeDept,
                center,
              },
            }
          }
        )

        map.getSource('dept').setData({
          type: 'FeatureCollection',
          features,
        })

        map.getSource('deptPoint').setData({
          type: 'FeatureCollection',
          features: Map(
            filter(this.stats.depts, (s) => {
              return s.total
            }),
            (s) => {
              const center = s.geomCenter
              return {
                type: 'Feature',
                geometry: center,
                properties: {
                  total: s.total,
                  code: s.codeDept,
                },
              }
            }
          ),
        })
        this.toggleBDOrtho()
        this.loading = false
      }, 100)
    },
    toggleBDOrtho() {
      this.$root.map.setLayoutProperty(
        'bdOrthoTiles',
        'visibility',
        'visible'
      )
      this.moveLayers(this.$root.map)
    },
    onDeptEnter({ target, features }) {
      this.setCursor(target, 'pointer')
      const feature = features[features.length - 1]
      this.popup
        .setLngLat(feature.geometry.coordinates)
        .setHTML(this.formatDept(features[0]))
        .addTo(target)
    },
    onDeptLeave({ target }) {
      this.setCursor(target, '')
      this.popup.remove()
    },
    async onDeptClick({ target, features }) {
      const properties = features[features.length - 1].properties
      const { department } = await clientApi.get(
        `boundaries/departments/${properties.code}`
      )

      const feature = {
        type: 'Feature',
        geometry: department.geom,
      }

      target.getSource('deptBoundary').setData({
        type: 'FeatureCollection',
        features: [mask(feature)],
      })

      this.selectedDept = properties.code
      await this.fitToBounds(department.geom)
      target.setLayoutProperty(
        'deptsBoundaries',
        'visibility',
        'none'
      )
      target.setLayoutProperty('deptLayer', 'visibility', 'none')

      const items = await clientApi.get(
        `fluxItems?type=${this.adsType}&state=validated&codeDept=${properties.code}${this.activeOnly ? '&structuredData.isActive=true' : ''
        }`
      )

      let f = Map(
        filter(items, (i) => {
          return i.finalGeom
        }),
        (i) => {
          return {
            type: 'Feature',
            geometry: centroid({
              type: 'Feature',
              geometry: i.finalGeom,
            }).geometry,
            properties: {
              ...{
                fluxItemId: i.id,
                finalAddress: i.finalAddress || null,
                source: i.source,
              },
              ...i.structuredData,
            },
          }
        }
      )

      target.getSource('items').setData({
        type: 'FeatureCollection',
        features: f,
      })

      f = Map(items, (i) => {
        return {
          type: 'Feature',
          geometry: i.finalGeom,
          properties: {
            fluxItemId: i.id,
            isActive: i.structuredData.isActive,
          },
        }
      })

      this.parcellesFeatures = f

      target.getSource('parcelles').setData({
        type: 'FeatureCollection',
        features: f,
      })
    },
    onItemEnter({ target, features }) {
      this.setCursor(target, 'pointer')
      const feature = features[features.length - 1]
      this.popup
        .setLngLat(feature.geometry.coordinates)
        .setHTML(this.formatItem(features[0]))
        .addTo(target)
    },
    onItemLeave({ target }) {
      this.setCursor(target, '')
      this.popup.remove()
    },
    async onItemClick({ features }) {
      const item = features[features.length - 1]
      const feats = this.parcellesFeatures
      const parcelle = find(feats, (f) => {
        return f.properties.fluxItemId === item.properties.fluxItemId
      })
      const params = { fluxItemId: item.properties.fluxItemId }
      await this.getFluxItem({ params })
      await this.fitToBounds(parcelle.geometry)
    },
    async outsideClick({ target }) {
      this.selectedDept = null
      target.getSource('deptBoundary').setData(this.emptyFeatures)
      target.getSource('items').setData(this.emptyFeatures)
      target.setLayoutProperty(
        'deptsBoundaries',
        'visibility',
        'visible'
      )
      target.setLayoutProperty('deptLayer', 'visibility', 'visible')
    },
    async toggleActiveOnly(v = null) {
      if (v !== null) {
        this.activeOnly = v
      } else {
        this.activeOnly = !this.activeOnly
      }
      this.loading = true
      const params = { active: this.activeOnly, adsType: this.adsType }
      await this.getStats({ params })
      this.loading = false
      this.setLayers(this.$root.map)
    },
  },
}
</script>
<style lang="scss">
.properties {
  list-style-type: none;

  li:nth-of-type(odd) {
    background: #f2f4f5;
  }
}

.total-ads {
  position: absolute;
  margin: 0px;
  font-size: 1.2em;
  padding: 8px;
  color: white;
  background: rgba(0, 0, 0, 0.5);
}

#map-loader {
  position: absolute;
  z-index: 100;
}

.active-only {
  position: absolute;
  padding-right: 0;
  background: rgba(0, 0, 0, 0.5);
  right: 0;
  bottom: 0;
}

#mapbox-wrapper {
  height: 100%;
  width: 100%;

  #mapbox-container {
    height: 100%;
    width: 100%;
  }
}
</style>
